import { useCallback, useEffect } from 'react';
import { WEAPON_ATTACK_PROFILES } from '../data/items';
import { SPELL_MAP }              from '../data/spells';
import { DASH_DURATION, DASH_COOLDOWN, BLOCK_COOLDOWN, BLOCK_IMMUNE_MS, PLAYER_SIZE, MIN_ATTACK_COOLDOWN } from '../constants';

/**
 * Registers global keydown/keyup listeners and game-world mouse listeners.
 * - Tracks mouse position + left-button state in stateRef.current.mouse
 * - Triggers melee attack on Q or Space
 * - Casts spells from stateRef.current.runSpells (in-run earned spells)
 * - Dash (E) fires in the direction of the mouse cursor
 *
 * Movement:
 *   Left-click held → player follows mouse cursor (handled in useGameLoop)
 *   Arrow keys still supported as an alternative movement scheme
 */
export function useKeyboard({ stateRef, setAttackRenderKey, onEscapeRef, onPotionUseRef, gameWorldRef }) {
  // ── Mouse tracking ──────────────────────────────────────────────────────────
  useEffect(() => {
    const getRelativePos = (e) => {
      const el = gameWorldRef?.current;
      if (!el) return null;
      const rect = el.getBoundingClientRect();
      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    };

    const onMouseMove = (e) => {
      const pos = getRelativePos(e);
      if (!pos) return;
      stateRef.current.mouse.x = pos.x;
      stateRef.current.mouse.y = pos.y;
    };

    const onMouseDown = (e) => {
      if (e.button !== 0) return; // left button only
      const pos = getRelativePos(e);
      if (pos) {
        stateRef.current.mouse.x = pos.x;
        stateRef.current.mouse.y = pos.y;
      }
      stateRef.current.mouse.down = true;
    };

    const onMouseUp = (e) => {
      if (e.button !== 0) return;
      stateRef.current.mouse.down = false;
    };

    // Attach to window so mouse-up is caught even when released outside the game world
    window.addEventListener('mousemove',  onMouseMove);
    window.addEventListener('mousedown',  onMouseDown);
    window.addEventListener('mouseup',    onMouseUp);
    return () => {
      window.removeEventListener('mousemove',  onMouseMove);
      window.removeEventListener('mousedown',  onMouseDown);
      window.removeEventListener('mouseup',    onMouseUp);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Keyboard bindings ───────────────────────────────────────────────────────
  useEffect(() => {
    const onKeyDown = (e) => {
      const key = e.key.toLowerCase();
      stateRef.current.keys[key] = true;

      // Prevent default browser scroll on arrow keys / spacebar
      if (['arrowup', 'arrowdown', 'arrowleft', 'arrowright', ' '].includes(key)) {
        e.preventDefault();
      }

      // ── Pause / Resume (Escape) ──────────────────────────────────────────────
      if (e.key === 'Escape') {
        e.preventDefault();
        onEscapeRef?.current?.();
        return;
      }

      // ── Melee attack: Q or Space ─────────────────────────────────────────────
      if (key === 'q' || key === ' ') {
        const s       = stateRef.current;
        const now     = performance.now();
        const profile = WEAPON_ATTACK_PROFILES[s.weaponType ?? 'unarmed'] ?? WEAPON_ATTACK_PROFILES.unarmed;
        const effectiveCd = Math.max(MIN_ATTACK_COOLDOWN, profile.baseCd * s.cdMult * (s.activeModifier?.playerCdMult ?? 1));
        if (s.alive && now - s.lastAttackTime >= effectiveCd) {
          s.attack         = { active: true, startTime: now, damageDealt: false };
          s.lastAttackTime = now;
          setAttackRenderKey(now);
        }
      }

      // ── Dash: E — direction toward mouse cursor ──────────────────────────────
      if (key === 'e') {
        const s   = stateRef.current;
        const now = performance.now();
        if (s.alive && !s.isDashing && now - s.lastDashTime >= DASH_COOLDOWN) {
          // Direction from player centre to current mouse position
          const px = s.player.x + PLAYER_SIZE / 2;
          const py = s.player.y + PLAYER_SIZE / 2;
          const dx = s.mouse.x - px;
          const dy = s.mouse.y - py;
          const mag = Math.sqrt(dx * dx + dy * dy) || 0;
          s.dashVelX     = mag > 0 ? dx / mag : 0;
          s.dashVelY     = mag > 0 ? dy / mag : -1; // default up if cursor is on player
          s.isDashing    = true;
          s.dashEndTime  = now + DASH_DURATION;
          s.lastDashTime = now;
        }
      }

      // ── HP Potion: R ─────────────────────────────────────────────────────────
      if (key === 'r') {
        onPotionUseRef?.current?.();
      }

      // ── Block: W — brief immunity + shield SVG (no enemy push) ──────────────
      if (key === 'w') {
        const s   = stateRef.current;
        const now = performance.now();
        const effectiveBlockCd = Math.max(100, BLOCK_COOLDOWN - (s.blockCooldownReduction ?? 0));
        if (s.alive && now - s.lastBlockTime >= effectiveBlockCd) {
          s.shieldUntil   = now + BLOCK_IMMUNE_MS + (s.blockImmuneMsBonus ?? 0);
          s.lastBlockTime = now;
        }
      }

      // ── Spell slots: 1–4 or Z/X/C/V ─────────────────────────────────────────
      const numSlot   = ['1', '2', '3', '4'].indexOf(e.key);
      const altSlot   = ['z', 'x', 'c', 'v'].indexOf(key);
      const slotIndex = numSlot >= 0 ? numSlot : altSlot;

      if (slotIndex >= 0) {
        const s = stateRef.current;
        if (!s.alive) return;
        const spellId = (s.runSpells ?? [])[slotIndex];
        if (!spellId) return;
        const spell = SPELL_MAP[spellId];
        if (!spell)  return;
        const now      = performance.now();
        const lastCast = s.spellCooldowns[spellId] ?? -Infinity;
        if (now - lastCast >= spell.cooldown) {
          spell.cast(s, now);
          s.spellCooldowns[spellId] = now;
        }
      }
    };

    const onKeyUp = (e) => {
      stateRef.current.keys[e.key.toLowerCase()] = false;
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup',   onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup',   onKeyUp);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Touch tracking (movement) ────────────────────────────────────────────
  useEffect(() => {
    const getRelativePos = (touch) => {
      const el = gameWorldRef?.current;
      if (!el) return null;
      const rect = el.getBoundingClientRect();
      return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
    };

    // One finger is dedicated to movement; track it by touch identifier
    let moveTouchId = null;

    const onTouchStart = (e) => {
      for (const touch of e.changedTouches) {
        // Skip touches that originated on a UI button
        if (touch.target?.closest?.('[data-mobile-btn]')) continue;
        // Only track one movement finger at a time
        if (moveTouchId !== null) continue;
        const pos = getRelativePos(touch);
        if (!pos) continue;
        moveTouchId = touch.identifier;
        stateRef.current.mouse.x    = pos.x;
        stateRef.current.mouse.y    = pos.y;
        stateRef.current.mouse.down = true;
      }
    };

    const onTouchMove = (e) => {
      for (const touch of e.changedTouches) {
        if (touch.identifier !== moveTouchId) continue;
        const pos = getRelativePos(touch);
        if (!pos) continue;
        stateRef.current.mouse.x = pos.x;
        stateRef.current.mouse.y = pos.y;
      }
    };

    const onTouchEnd = (e) => {
      for (const touch of e.changedTouches) {
        if (touch.identifier !== moveTouchId) continue;
        moveTouchId = null;
        stateRef.current.mouse.down = false;
      }
    };

    window.addEventListener('touchstart',  onTouchStart,  { passive: true });
    window.addEventListener('touchmove',   onTouchMove,   { passive: true });
    window.addEventListener('touchend',    onTouchEnd,    { passive: true });
    window.addEventListener('touchcancel', onTouchEnd,    { passive: true });
    return () => {
      window.removeEventListener('touchstart',  onTouchStart);
      window.removeEventListener('touchmove',   onTouchMove);
      window.removeEventListener('touchend',    onTouchEnd);
      window.removeEventListener('touchcancel', onTouchEnd);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Mobile action helpers (returned for on-screen button use) ────────────
  const triggerAttack = useCallback(() => {
    const s   = stateRef.current;
    const now = performance.now();
    const profile     = WEAPON_ATTACK_PROFILES[s.weaponType ?? 'unarmed'] ?? WEAPON_ATTACK_PROFILES.unarmed;
    const effectiveCd = Math.max(MIN_ATTACK_COOLDOWN, profile.baseCd * (s.cdMult ?? 1) * (s.activeModifier?.playerCdMult ?? 1));
    if (s.alive && now - s.lastAttackTime >= effectiveCd) {
      s.attack         = { active: true, startTime: now, damageDealt: false };
      s.lastAttackTime = now;
      setAttackRenderKey(now);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const triggerDash = useCallback(() => {
    const s   = stateRef.current;
    const now = performance.now();
    if (s.alive && !s.isDashing && now - s.lastDashTime >= DASH_COOLDOWN) {
      const px = s.player.x + PLAYER_SIZE / 2;
      const py = s.player.y + PLAYER_SIZE / 2;
      const dx = s.mouse.x - px;
      const dy = s.mouse.y - py;
      const mag = Math.sqrt(dx * dx + dy * dy) || 0;
      s.dashVelX     = mag > 0 ? dx / mag : 0;
      s.dashVelY     = mag > 0 ? dy / mag : -1;
      s.isDashing    = true;
      s.dashEndTime  = now + DASH_DURATION;
      s.lastDashTime = now;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const triggerCast = useCallback((slotIndex) => {
    const s = stateRef.current;
    if (!s.alive) return;
    const spellId = (s.runSpells ?? [])[slotIndex];
    if (!spellId) return;
    const spell = SPELL_MAP[spellId];
    if (!spell) return;
    const now      = performance.now();
    const lastCast = s.spellCooldowns[spellId] ?? -Infinity;
    if (now - lastCast >= spell.cooldown) {
      spell.cast(s, now);
      s.spellCooldowns[spellId] = now;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const triggerPause = useCallback(() => {
    onEscapeRef?.current?.();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const triggerBlock = useCallback(() => {
    const s   = stateRef.current;
    const now = performance.now();
    const effectiveBlockCd = Math.max(100, BLOCK_COOLDOWN - (s.blockCooldownReduction ?? 0));
    if (s.alive && now - s.lastBlockTime >= effectiveBlockCd) {
      s.shieldUntil   = now + BLOCK_IMMUNE_MS + (s.blockImmuneMsBonus ?? 0);
      s.lastBlockTime = now;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const triggerPotion = useCallback(() => {
    onPotionUseRef?.current?.();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { triggerAttack, triggerDash, triggerCast, triggerPause, triggerBlock, triggerPotion };
}
